import { getIn, useFormikContext } from 'formik';

import { ProductContext } from '../../../../types/ProductContext';
import { FormikAgentSelectFlow } from '../../../agent/AgentSelectFlow/FormikAgentSelectFlow';
import { Callout } from '../../../cdl/Callout/Callout';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { ReadOnlyField } from '../../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../../cdl/Text/Text';
import { FormikDatePicker } from '../../../common/form-elements/formik/FormikDatePicker';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { FormikEnumOptionSelect } from '../../../common/form-elements/formik/FormikEnumOptionSelect';
import { FormikFormattedNumberInput } from '../../../common/FormattedNumberInput/FormikFormattedNumberInput';
import { FormikProductTable } from '../../../common/FormikProductTable/FormikProductTable';
import { startOfUtcDayIso } from '../../../common/helpers/dateFns.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { Box } from '../../../common/ui/Box';
import { OfferModel } from '../../model/OfferModel';
import { OfferState } from '../../model/OfferState';
import { PortDisplay } from '../components/PortDisplay';

export const OfferEditForm = () => {
    const {
        values: offer,
        initialValues,
        setFieldValue,
        errors,
        touched,
        submitCount,
    } = useFormikContext<OfferModel>();
    const role = useRole();
    const duplicateItemsError = getIn(errors, 'duplicateItemsError');
    const showDuplicateItemsError = Boolean(duplicateItemsError && (submitCount > 0 || touched?.items));
    const hasSingleInitiallyUnavailableProduct =
        role.isCustomer() &&
        initialValues.state === OfferState.SUPPLIER_ADJUSTED &&
        initialValues.items.length === 1 &&
        initialValues.items[0].deleted;

    const reactivateUnavailableProduct = (portId: string, dateDelivery: string) => {
        if (!hasSingleInitiallyUnavailableProduct) {
            return;
        }

        const portChanged = portId !== initialValues.portId;
        const deliveryDateChanged =
            !!dateDelivery && startOfUtcDayIso(dateDelivery) !== startOfUtcDayIso(initialValues.dateDelivery);

        if (portChanged || deliveryDateChanged) {
            setFieldValue('items.0.deleted', false, false);
        }
    };

    return (
        <Box display="grid" padding={32} gap={32}>
            <Box display="flex" flexDirection="column" gap={8}>
                <Text variant="title">{translate('offer.edit.lubes.generalInformation')}</Text>
                {hasSingleInitiallyUnavailableProduct ? (
                    <Callout
                        heading={translate('offer.edit.lubes.productUnavailable.title')}
                        description={translate('offer.edit.lubes.productUnavailable.description')}
                    />
                ) : null}
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(3, minmax(0,1fr))"
                    gridTemplateRows={role.isCustomer() ? 'auto 100px' : 'auto'}
                    rowGap={24}
                    columnGap={16}
                >
                    {role.isSupplier() ? (
                        <ReadOnlyField value={offer.customer.name} label={translate('offer.edit.lubes.customer')} />
                    ) : (
                        <ReadOnlyField value={offer.supplier.name} label={translate('offer.edit.lubes.supplier')} />
                    )}

                    <ReadOnlyField value={offer.vessel.name} label={translate('offer.edit.lubes.vessel')} />

                    {offer.port ? (
                        <PortDisplay offer={offer} onDeliveryDetailsChange={reactivateUnavailableProduct} />
                    ) : null}

                    <div>
                        <FormikDatePicker
                            name="dateDelivery"
                            label={translate('offer.edit.lubes.deliveryDate')}
                            onChange={(dateDelivery) => reactivateUnavailableProduct(offer.portId, dateDelivery)}
                        />
                    </div>

                    {role.isCustomer() ? (
                        <>
                            <FormikInput
                                name="buyerReference"
                                label={translate('offer.edit.lubes.buyerReference')}
                                markAsOptional
                            />
                            <FormikAgentSelectFlow
                                name="agentId"
                                customerId={offer.customerId}
                                portId={offer.portId}
                                label={translate('offer.edit.lubes.agent')}
                                markAsOptional
                            />
                        </>
                    ) : null}

                    {role.isSupplier() ? (
                        <>
                            {offer.isSpot() ? (
                                <Box display="grid" gridTemplateColumns="3fr 1fr" gap={8}>
                                    <FormikEnumOptionSelect
                                        label={translate('offer.edit.lubes.paymentTerms')}
                                        name="paymentTermReference"
                                        enumType="paymentTermReference"
                                        onOptionChange={(option) => {
                                            if (option === 'PREPAYMENT') {
                                                offer.paymentTermReferenceDays = undefined;
                                            }
                                        }}
                                    />
                                    <FormikFormattedNumberInput
                                        name="paymentTermReferenceDays"
                                        label={translate('offer.edit.lubes.paymentTermsDays')}
                                        allowNegativeValue={false}
                                        allowDecimals={false}
                                        disabled={offer.paymentTermReference === 'PREPAYMENT'}
                                    />
                                </Box>
                            ) : null}

                            <FormikFormattedNumberInput
                                name="noticeDays"
                                label={translate('offer.edit.lubes.noticeDays')}
                                allowNegativeValue={false}
                                allowDecimals={false}
                                markAsOptional
                            />
                            <FormikEnumOptionSelect
                                name="supplyMode"
                                enumType="supplyMode"
                                label={translate('offer.edit.lubes.supplyMode')}
                                context={ProductContext.LUBES}
                            />
                            <FormikInput
                                name="vendorReference"
                                label={translate('offer.edit.lubes.vendorReference')}
                                markAsOptional
                            />
                        </>
                    ) : null}
                </Box>
            </Box>

            <Box>
                <Text variant="title">Products</Text>
                <FormikProductTable
                    config={{
                        showPrices: true,
                        editable: true,
                        editPrices: role.isSupplier() || (role.isCustomer() && offer.state === OfferState.DELIVERED),
                        editSurcharges:
                            role.isSupplier() || (role.isCustomer() && offer.state === OfferState.DELIVERED),
                        initialItems: offer.items,
                    }}
                />
                {showDuplicateItemsError ? (
                    <Box marginTop={16}>
                        <Callout variant="negative" description={duplicateItemsError} Icon={IconInfoCircle} />
                    </Box>
                ) : null}
            </Box>
            <FormikDebug />
        </Box>
    );
};
